import { camelCase, merge, mergeWith } from "lodash";
import React from "react";
import LiveAveragePricePerAcre from "../analysis/LiveAveragePricePerAcre";
import LiveAveragePricePerSqft from "../analysis/LiveAveragePricePerSqft";
import LiveAveragePricing from "../analysis/LiveAveragePricing";
import LiveEstimatedMarketValue from "../analysis/LiveEstimatedMarketValue";
import LiveOnlineValuations from "../analysis/LiveOnlineValuations";
import LiveTimeToSell from "../analysis/LiveTimeToSell";
import IconCalendar from "../icons/IconCalendar";
import IconForSale from "../icons/IconForSale";
import IconMoney from "../icons/IconMoney";
import IconPricePerSqft from "../icons/IconPricePerSqft";
import IconRobot from "../icons/IconRobot";
import LivePreviewAveragePricePerAcre from "../previews/LivePreviewAveragePricePerAcre";
import LivePreviewAveragePricePerSqft from "../previews/LivePreviewAveragePricePerSqft";
import LivePreviewAveragePricing from "../previews/LivePreviewAveragePricing";
import LivePreviewEstimatedMarketValue from "../previews/LivePreviewEstimatedMarketValue";
import LivePreviewOnlineValuations from "../previews/LivePreviewOnlineValuations";
import LivePreviewTimeToSell from "../previews/LivePreviewTimeToSell";

export const createModules = ({ guid, links = {}, pages = [], isWebLead }) => {
  const pageMap = pages.reduce(
    (byKey, page) => ({ ...byKey, [page.key]: page }),
    {}
  );
  const modules = [
    {
      key: "average-pricing",
      title: "Average Pricing",
      url: `/live/${guid}/average-pricing`,
      path: "/live/:guid/average-pricing",
      isHidden: false,
      canHide: true,
      subModules: []
    },
    {
      key: "average-price-per-sqft",
      title: "Average Price/Sq Ft",
      url: `/live/${guid}/average-price-per-sqft`,
      path: "/live/:guid/average-price-per-sqft",
      isHidden: false,
      canHide: true,
      subModules: []
    },
    {
      key: "average-price-per-acre",
      title: "Average Price/Acre",
      url: `/live/${guid}/average-price-per-acre`,
      path: "/live/:guid/average-price-per-acre",
      isHidden: false,
      canHide: true,
      subModules: []
    },
    {
      key: "time-to-sell",
      title: "Time to Sell",
      url: `/live/${guid}/time-to-sell`,
      path: "/live/:guid/time-to-sell",
      isHidden: false,
      canHide: true,
      subModules: []
    },
    {
      key: "online-valuations",
      title: "Online Valuations",
      url: `/live/${guid}/online-valuations`,
      path: "/live/:guid/online-valuations",
      isHidden: false,
      canHide: true,
      subModules: []
    },
    {
      key: "suggested-list-price",
      title: "Suggested List Price",
      url: `/live/${guid}/suggested-list-price`,
      path: "/live/:guid/suggested-list-price",
      isHidden: false,
      canHide: true,
      subModules: []
    }
  ]
    .filter((module) => (isWebLead ? !!pageMap[module.key] : true))
    .map((module) => {
      const page = pageMap[module.key];
      return mergeWith({}, module, page, (a, b) => b ?? a);
    });
  const modifiedLinks = getModifiedLinks(modules, links);
  const newModules = modules.map((module) => {
    const transformedModule = transformModule(module);
    const camelCaseKey = camelCase(transformedModule.key);
    const linkData = modifiedLinks[camelCaseKey] || {};
    const { key, ...linkDataWithoutKey } = linkData;
    const page = pages.find((page) => page.key === module.key) || {};

    return {
      ...transformedModule,
      ...linkDataWithoutKey,
      config: page.config || {}
    };
  });
  return newModules;
};

export function transformModule(data) {
  return {
    key: data.key,
    title: data.title,
    footerTitle: data.footerTitle ?? data.title,
    url: data.url,
    path: data.path,
    isHidden: data.isHidden,
    canHide: data.canHide ?? true,
    el: getElement(data),
    previewEl: getPreviewElement(data),
    iconEl: getIconElement(data),
    showSparkle: !!data.showSparkle,
    subModules: [],
    data
  };
}

// Only a stub for now
export function transformSubModule(data) {
  return data;
}

function getModifiedLinks(data, links) {
  const newLinks = data.reduce((byKey, page) => {
    const camelCaseKey = camelCase(page.key);
    return {
      ...byKey,
      [page.key]: {
        isHidden: !!(links[camelCaseKey] || {}).isHidden
      }
    };
  }, {});
  return merge({}, links, newLinks);
}

function getElement(page) {
  switch (page.key) {
    case "average-pricing":
      return <LiveAveragePricing />;
    case "average-price-per-sqft":
      return <LiveAveragePricePerSqft />;
    case "average-price-per-acre":
      return <LiveAveragePricePerAcre />;
    case "time-to-sell":
      return <LiveTimeToSell />;
    case "online-valuations":
      return <LiveOnlineValuations />;
    case "suggested-list-price":
      return <LiveEstimatedMarketValue />;
    default:
      throw new Error("Invalid key");
  }
}

function getPreviewElement(page) {
  switch (page.key) {
    case "average-pricing":
      return <LivePreviewAveragePricing title={page.title} />;
    case "average-price-per-sqft":
      return <LivePreviewAveragePricePerSqft title={page.title} />;
    case "average-price-per-acre":
      return <LivePreviewAveragePricePerAcre title={page.title} />;
    case "time-to-sell":
      return <LivePreviewTimeToSell title={page.title} />;
    case "online-valuations":
      return <LivePreviewOnlineValuations title={page.title} />;
    case "suggested-list-price":
      return <LivePreviewEstimatedMarketValue title={page.title} />;
    default:
      throw new Error("Invalid key");
  }
}

function getIconElement(page) {
  switch (page.key) {
    case "average-pricing":
      return <IconMoney />;
    case "average-price-per-sqft":
    case "average-price-per-acre":
      return <IconPricePerSqft />;
    case "time-to-sell":
      return <IconCalendar />;
    case "online-valuations":
      return <IconRobot />;
    case "suggested-list-price":
      return <IconForSale />;
    default:
      throw new Error("Invalid key");
  }
}
